import { Pipe, PipeTransform } from '@angular/core';
import { isDefined } from '@freelancer/utils';

const MIN_DENOMINATOR_MULTIPLIER = 1;

const NUM_ZERO_PER_GROUP = 3;
const NUM_ZERO_GROUP_THOUSAND = 2;
const NUM_ZERO_GROUP_MILLION = 3;
const MAX_NUM_ZERO_GROUP = NUM_ZERO_GROUP_MILLION;

/**
 * TODO: Support localization T237419
 *
 * This pipe transforms a number to the compact decimal format.
 * Currently, the compact logic supports from ones up to the hundred-millions,
 * anything bigger will only be truncated up to the hundred-millions place.
 *
 * This pipe always:
 * - removes decimal numbers
 * - rounds down the value to the nearest highest place value
 *
 *
 * Examples:
 *
 * 5 => '0'
 * 10 => '10'
 * 97 => '90'
 * 190 => '100'
 * 1890 => '1K'
 * 2500 => '2K'
 * 54321 => '50K'
 * 698221 => '600K'
 * 1499211 => '1M'
 * 68000101 =>  '60M'
 * 848101292 => '800M'
 *
 * Over hundred million:
 * 3999000111 => 3000M
 *
 * Numbers with decimal places
 * 1234.234 => 1K
 *
 * Negative numbers work the same as positive numbers.
 */
@Pipe({ name: 'compactNumber' })
export class CompactNumberPipe implements PipeTransform {
  transform(value: number | undefined): string | undefined {
    if (!isDefined(value)) {
      return undefined;
    }

    const newValue = Math.floor(Math.abs(value));
    const stringValue = newValue.toString();
    const numZeroGroups = Math.ceil(stringValue.length / NUM_ZERO_PER_GROUP);
    const placeValueSymbol = this.getPlaceValueSymbol(numZeroGroups);

    const roundDownDenominator =
      10 ** (stringValue.length - 1 || MIN_DENOMINATOR_MULTIPLIER);
    const roundDownAmount = newValue - (newValue % roundDownDenominator);

    const numZeroesToTruncate =
      ((numZeroGroups > MAX_NUM_ZERO_GROUP
        ? MAX_NUM_ZERO_GROUP
        : numZeroGroups) -
        1) *
      3;

    const truncatedAmount =
      roundDownAmount.toString().slice(0, -numZeroesToTruncate) ||
      roundDownAmount;

    const sign = value < 0 && truncatedAmount > 0 ? '-' : '';

    return `${sign}${truncatedAmount}${placeValueSymbol}`;
  }

  getPlaceValueSymbol(numZeroGroups: number) {
    if (numZeroGroups >= NUM_ZERO_GROUP_MILLION) {
      return 'M';
    }

    if (numZeroGroups >= NUM_ZERO_GROUP_THOUSAND) {
      return 'K';
    }
    return '';
  }
}
